import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Box,
  useMediaQuery,
  IconButton,
  Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FeedbackIcon from '@mui/icons-material/Feedback';
import { FeedbackButtonContainer } from './styled';
import { sendFeedback } from 'api';

const FeedbackComponent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleClickOpen = () => {
    setOpen(true);
    setSubmitAttempted(false);
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setSubject('');
    setMessage('');
    setSubmitAttempted(false);
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    if (!subject.trim() || !message.trim()) return;

    setLoading(true);
    try {
      await sendFeedback({
        subject: subject.trim(),
        message: message.trim(),
        resource: window.location.pathname
      });

      setSnackbar({
        open: true,
        message: 'Thank you for your feedback!',
        severity: 'success'
      });
      handleClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSnackbar({
        open: true,
        message: 'Failed to submit feedback. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const renderButton = () => {
    if (isMobile) {
      return (
        <Tooltip title="Send Feedback" placement="left">
          <IconButton
            onClick={handleClickOpen}
            sx={{
              backgroundColor: (theme) => theme.palette.primary.main,
              color: '#fff',
              width: '48px',
              height: '48px',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.primary.dark,
              },
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
          >
            <FeedbackIcon />
          </IconButton>
        </Tooltip>
      );
    }

    return (
      <Button
        onClick={handleClickOpen}
        variant="contained"
        sx={{
          width: '32px',
          minWidth: '20px !important', // Override MUI default
          minHeight: '110px',
          padding: '2px 2px',
          textTransform: 'none',
          backgroundColor: (theme) => theme.palette.primary.main,
          '&:hover': {
            backgroundColor: (theme) => theme.palette.primary.dark,
          },
          borderRadius: '14px 0 0 14px', // Changed to face outside
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          // New approach for vertical text
          '& .MuiButton-label': {
            display: 'block',
            transform: 'rotate(-90deg)',
            letterSpacing: '1px',
            whiteSpace: 'nowrap',
          }
        }}
      >
        <Box
          sx={{
            transform: 'rotate(-90deg)',
            letterSpacing: '1px',
            whiteSpace: 'nowrap',
          }}
        >
          Feedback
        </Box>
      </Button>
    );
  };

  return (
    <>
      <FeedbackButtonContainer>
        {renderButton()}
      </FeedbackButtonContainer>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Feedback</DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Share your thoughts with us to help improve our service.
          </DialogContentText>

          <TextField
            autoFocus
            margin="dense"
            label="Subject"
            fullWidth
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={loading}
            error={submitAttempted && !subject.trim()}
            helperText={submitAttempted && !subject.trim() ? 'Subject is required' : ''}
            sx={{ mb: 2 }}
          />

          <TextField
            label="Message"
            fullWidth
            multiline
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={loading}
            error={submitAttempted && !message.trim()}
            helperText={submitAttempted && !message.trim() ? 'Message is required' : ''}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={handleClose}
            color="inherit"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !subject.trim() || !message.trim()}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            Submit Feedback
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FeedbackComponent;